<template>
  <div class="login-page" v-if="!props.type">
    <div class="login-card">
      <!-- Panel de marca + formulario -->
      <div class="login-brand">
        <div class="brand-badge"><i class="pi pi-map" /></div>
        <div class="brand-eyebrow">Bienvenido</div>
        <h1 class="brand-title">Gestión de Territorios</h1>

        <!-- Formulario de login -->
        <div class="login-form" v-if="!userStore.isLoggedIn && step !== 'totp'">
          <div class="field">
            <label for="email">Nombre de usuario</label>
            <InputText
              v-model="email"
              id="email"
              class="w-full"
              placeholder="tu usuario"
              autocomplete="off"
            />
          </div>
          <div class="field">
            <label for="pass">Contraseña</label>
            <InputText
              v-model="pass"
              class="w-full"
              placeholder="••••••••"
              type="password"
              @keyup.enter="login"
            />
          </div>
          <div v-if="error" class="login-error">{{ error }}</div>
          <Button label="Ingresar" @click="login" :loading="loading" class="w-full" />
        </div>

        <!-- Paso TOTP -->
        <div class="login-form" v-if="step === 'totp'">
          <h3 class="step-title">Verificación en dos pasos</h3>
          <p class="step-desc">
            Ingresa el código de 6 dígitos de tu aplicación de autenticación.
          </p>
          <div class="field">
            <label for="totpCode">Código de autenticación</label>
            <InputText
              v-model="totpCode"
              id="totpCode"
              class="w-full totp-input"
              placeholder="000000"
              maxlength="6"
              @keyup.enter="verifyTotp"
            />
          </div>
          <div v-if="error" class="login-error">{{ error }}</div>
          <div class="flex gap-2">
            <Button label="Verificar" @click="verifyTotp" :loading="loading" class="flex-1" />
            <Button label="Volver" severity="secondary" outlined @click="step = null" />
          </div>
        </div>

        <!-- Cargando datos -->
        <div class="login-form" v-if="userStore.isLoggedIn && !crypto.ready">
          <WaitingBible :full="false" :center="false" />
        </div>

        <div v-if="!userStore.isLoggedIn && step !== 'totp'" class="signup-note">
          <span>¿No tienes usuario?</span>
          <a
            href="https://api.whatsapp.com/send?phone=5492644766180&text=Hola,%20tengo%20una%20consulta%20sobre%20el%20sistema%20de%20territorios."
            target="_blank"
            rel="noopener noreferrer"
          >
            Solicitar acceso
          </a>
        </div>
      </div>

      <!-- Panel de seguridad -->
      <div class="login-info">
        <div class="info-block">
          <i class="pi pi-lock info-icon" />
          <div>
            <h4>Cifrado en tu dispositivo</h4>
            <p>
              Los datos se cifran directamente en tu dispositivo antes de
              enviarse al servidor. Nadie más —ni siquiera nosotros— puede ver o
              modificar tu información sin tu permiso.
            </p>
          </div>
        </div>
        <div class="info-block">
          <i class="pi pi-shield info-icon" />
          <div>
            <h4>Verificación en dos pasos</h4>
            <p>
              Podés activar 2FA desde la configuración. Con 2FA necesitarás tu
              contraseña <b>más</b> un código de tu app de autenticación para
              ingresar.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="" v-if="props.type">
    <Button
      icon="pi pi-bars"
      @click="screen.showSidebar = !screen.showSidebar"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, defineProps } from "vue";
import { useStorage } from "@/composables/useStorage";
import { useEncryptStore } from "@/store/useEncryptStore";
import { useUserStore } from "@/store/useUserStore";
import { usePresenceStore } from "@/store/usePresenceStore";
import { useApi } from "@/composables/useApi";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import WaitingBible from "@/components/WaitingBible.vue";

const props = defineProps({ type: String });

const { getData } = useStorage();
const crypto = useEncryptStore();
const userStore = useUserStore();
const presenceStore = usePresenceStore();
const api = useApi();

const email = ref("");
const pass = ref("");
const totpCode = ref("");
const tempToken = ref(null);
const step = ref(null); // null | 'totp'
const error = ref(null);
const loading = ref(false);

const showError = (msg) => {
  error.value = msg;
  setTimeout(() => { error.value = null; }, 5000);
};

const finishLogin = (result) => {
  userStore.setUser(result);
  getData();
  if (result.congregationId) {
    presenceStore.connect();
  }
};

const login = async () => {
  if (!email.value || !pass.value) return;
  loading.value = true;
  error.value = null;
  try {
    const result = await api.post('/api/auth/login', { email: email.value, password: pass.value });
    if (result.error) { showError(result.error); return; }
    if (result.requiresTOTP) {
      tempToken.value = result.tempToken;
      step.value = 'totp';
      return;
    }
    finishLogin(result);
  } catch {
    showError("Error, por favor intenta nuevamente");
  } finally {
    loading.value = false;
  }
};

const verifyTotp = async () => {
  if (!totpCode.value) return;
  loading.value = true;
  error.value = null;
  try {
    const result = await api.post('/api/auth/totp/verify', { tempToken: tempToken.value, code: totpCode.value });
    if (result.error) { showError(result.error); return; }
    finishLogin(result);
  } catch {
    showError("Error al verificar el código");
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (userStore.isLoggedIn && !crypto.ready) {
    getData();
  }
});
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  background: var(--bg-grouped);
}

.login-card {
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 920px;
  background: var(--bg-primary);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-3);
  overflow: hidden;
}

/* Panel de marca con gradiente de acento */
.login-brand {
  flex: 1 1 0;
  padding: var(--space-7) var(--space-6);
  background: linear-gradient(160deg, var(--accent) 0%, #0a5fd0 100%);
  color: #fff;
  text-align: left;
}
.brand-badge {
  width: 52px;
  height: 52px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-4);
}
.brand-badge i {
  font-size: 1.5rem;
  color: #fff;
}
.brand-eyebrow {
  font-size: var(--text-footnote);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.85;
}
.brand-title {
  font-size: var(--text-large-title);
  font-weight: var(--weight-bold);
  letter-spacing: -0.02em;
  margin: var(--space-1) 0 var(--space-6);
  color: #fff;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.field label {
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
  color: rgba(255, 255, 255, 0.92);
}
.step-title {
  color: #fff;
  margin: 0;
}
.step-desc {
  font-size: var(--text-callout);
  opacity: 0.85;
  margin: 0;
}
.totp-input {
  letter-spacing: 0.4em;
  text-align: center;
  font-size: var(--text-title-2);
}

.login-error {
  background: rgba(255, 255, 255, 0.95);
  color: var(--danger);
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
}

.signup-note {
  margin-top: var(--space-5);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--text-callout);
  color: rgba(255, 255, 255, 0.88);
}
.signup-note a {
  color: #fff;
  font-weight: var(--weight-semibold);
  text-decoration: underline;
}

/* Panel de seguridad */
.login-info {
  flex: 1 1 0;
  padding: var(--space-7) var(--space-6);
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  justify-content: center;
}
.info-block {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
}
.info-icon {
  font-size: 1.25rem;
  color: var(--accent);
  margin-top: 2px;
}
.info-block h4 {
  margin: 0 0 var(--space-1);
}
.info-block p {
  margin: 0;
  font-size: var(--text-callout);
  color: var(--label-secondary);
  line-height: 1.5;
}

@media (max-width: 768px) {
  .login-card {
    flex-direction: column;
    max-width: 460px;
  }
  .login-info {
    border-top: 1px solid var(--separator);
  }
}
</style>
